import React, { useState } from "react";
import { ModalBody, ModalFooter, ModalHeader } from "reactstrap";
import {
    StyledH2,
    StyledLabel,
    StyledModal,
    StyledTextInput,
} from "@aureskonnect/react-ui";
import { useTranslation } from "react-i18next";
import classnames from "classnames";
import { AvForm } from "availity-reactstrap-validation";

import { capitalize } from "@helpers/general";
import { useFetchGlobal } from "../../../../api/hooks";

import { CustomMainColorButton } from "@components/Common/CustomMainColorButton";
import { CustomSecondaryColorButton } from "@components/Common/CustomSecondaryColorButton";
import ErrorToast from "@components/Common/ErrorTost";
import { FieldError } from "./components/FieldError";

type SupportCodeModalType = {
    isModalOpened: boolean;
    setIsModalOpened: Function;
    codeSupport: string;
    setCodeSupport: Function;
    isValidCode: boolean;
    setIsValidCode: Function;
    isConfig?: boolean;
    setIsModalConfigOpened?: Function;
    title?: string;
};

type ValidateSupportCodeResponse = {
    valid: boolean;
};

type ValidationOutcome = "valid" | "invalid" | "error";

export function SupportCodeModal({
    isModalOpened,
    setIsModalOpened,
    codeSupport,
    setCodeSupport,
    isValidCode,
    setIsValidCode,
    isConfig,
    setIsModalConfigOpened,
    title,
}: SupportCodeModalType) {
    const { t } = useTranslation();
    const { fetchGlobal } = useFetchGlobal();
    const [isValidating, setIsValidating] = useState<boolean>(false);

    const validateSupportCodeOnBackend = async (): Promise<
        ValidationOutcome
    > => {
        try {
            const result = await fetchGlobal<ValidateSupportCodeResponse>(
                `${process.env.REACT_APP_API_V2_URL}/validate-support-code`,
                {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({ code: codeSupport }),
                }
            );
            return result.valid === true ? "valid" : "invalid";
        } catch {
            return "error";
        }
    };

    const handleValidate = async () => {
        if (isValidating) return;
        setIsValidating(true);
        try {
            const outcome = await validateSupportCodeOnBackend();
            if (outcome === "valid") {
                if (isConfig === true) {
                    setIsModalConfigOpened?.(true);
                    setCodeSupport("");
                } else {
                    setIsValidCode(false);
                }
                setIsModalOpened(false);
            } else if (outcome === "invalid") {
                setIsValidCode(true);
            } else {
                ErrorToast(t("Validation service unavailable, please retry"));
            }
        } finally {
            setIsValidating(false);
        }
    };

    return (
        <StyledModal
            isOpen={isModalOpened}
            centered
            className="supportCode__clz"
        >
            <ModalHeader
                className="text-capitalize mnt_icn_close"
                toggle={() => {
                    setIsModalOpened(!isModalOpened);
                    setCodeSupport("");
                    setIsValidCode(false);
                }}
            >
                <StyledH2>
                    {capitalize(title || t("Modification mode"))}
                </StyledH2>
            </ModalHeader>
            <ModalBody>
                <AvForm>
                    <StyledLabel
                        htmlFor="support-code"
                        style={{ marginLeft: "100px" }}
                    >
                        {t("Support code")}
                    </StyledLabel>
                    <StyledTextInput
                        id="support-code"
                        name="support-code"
                        data-testid="support-code-input"
                        placeholder={t("Write")}
                        value={codeSupport}
                        onChange={(e: any) => {
                            setCodeSupport(e.target.value);
                            setIsValidCode(false);
                        }}
                        disabled={isValidating}
                        style={{ width: "250px", marginLeft: "100px" }}
                        className={classnames("cmn_inp_codeSupport ", {
                            input__clz: isValidCode,
                        })}
                    />
                    {isValidCode ? (
                        <FieldError style={{ color: "red", marginLeft: "22%" }}>
                            {t("The code is invalid")}
                        </FieldError>
                    ) : null}
                </AvForm>
            </ModalBody>
            <ModalFooter className="border-top-0 ">
                <CustomSecondaryColorButton
                    outline
                    variant="light"
                    onClick={() => {
                        setCodeSupport("");
                        setIsValidCode(false);
                        setIsModalOpened(false);
                    }}
                    rounded
                    className="mnt_btn_cancelAlert"
                    data-testid="support-code-cancel-btn"
                    disabled={isValidating}
                >
                    {t("Cancel")}
                </CustomSecondaryColorButton>
                <CustomMainColorButton
                    rounded
                    className="mr-2 cmn_btn_boutonValiderAlert"
                    variant="primary"
                    onClick={handleValidate}
                    data-testid="support-code-validate-btn"
                    disabled={isValidating || !codeSupport}
                >
                    {isValidating ? t("Validating...") : t("Validate")}
                </CustomMainColorButton>
            </ModalFooter>
        </StyledModal>
    );
}
